import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, CalendarDays, MapPin, Users, Receipt, Package, Settings, LogOut, ExternalLink, GraduationCap, CreditCard, Star, Send, BarChart3, ShieldCheck, Sun, Moon, Menu, ListChecks, ShoppingBag, Bell, Map, FileBarChart, History } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";

const nav = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/dashboard/notifications", label: "Notifications", icon: Bell },
  { to: "/dashboard/clients", label: "Customers", icon: Users },
  { to: "/dashboard/schedule", label: "Classes & Locations", icon: MapPin },
  { to: "/dashboard/locations", label: "Locations", icon: Map },
  { to: "/dashboard/appointments", label: "Bookings", icon: CalendarDays },
  { to: "/dashboard/waitlist", label: "Waitlist", icon: ListChecks },
  { to: "/dashboard/instructors", label: "Instructors", icon: GraduationCap },
  { to: "/dashboard/orders", label: "Orders", icon: ShoppingBag },
  { to: "/dashboard/payments", label: "Payments", icon: CreditCard },
  { to: "/dashboard/invoices", label: "Invoices", icon: Receipt },
  { to: "/dashboard/bundles", label: "Memberships", icon: Package },
  { to: "/dashboard/reviews", label: "Reviews", icon: Star },
  { to: "/dashboard/messaging", label: "Messaging", icon: Send },
  { to: "/dashboard/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/dashboard/reports", label: "Reports", icon: FileBarChart },
  { to: "/dashboard/settings", label: "Settings", icon: Settings },
  { to: "/dashboard/security", label: "Security", icon: ShieldCheck },
  { to: "/dashboard/audit", label: "Audit Log", icon: History },
];

export default function DashLayout() {
  const { logout, user } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const check = () => api.get("/admin/notifications").then((r) => {
      const seen = localStorage.getItem("pnp_notif_seen") || "";
      setUnread((r.data || []).filter((n) => (n.at || "") > seen).length);
    }).catch(() => {});
    check();
    const id = setInterval(check, 60000);
    return () => clearInterval(id);
  }, []);

  const markSeen = () => { localStorage.setItem("pnp_notif_seen", new Date().toISOString()); setUnread(0); };
  const doLogout = async () => { await logout(); navigate("/login"); };

  const SidebarInner = (
    <>
      <div className="px-6 py-7 border-b border-white/10">
        <Link to="/" className="font-display text-lg tracking-[0.15em] text-white">PAWS <span className="text-brand-red">N</span> POSES</Link>
        <div className="text-[10px] tracking-[0.3em] text-white/40 mt-1 uppercase">Admin Console</div>
      </div>
      <nav className="flex-1 px-4 py-4 space-y-0.5 overflow-y-auto">
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => { setOpen(false); if (n.label === "Notifications") markSeen(); }} className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-brand-red text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`} data-testid={`nav-${n.label.toLowerCase().replace(/\s|&/g, "-")}`}>
            <n.icon className="w-4 h-4 shrink-0" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-white/10 space-y-0.5">
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors"><ExternalLink className="w-4 h-4" /> View public site</a>
        <button onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors" data-testid="logout-btn"><LogOut className="w-4 h-4" /> Sign out</button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-brand-mist dark:bg-neutral-950">
      <aside className="w-64 shrink-0 bg-brand-ink dark:bg-black flex-col hidden md:flex fixed inset-y-0 z-40">{SidebarInner}</aside>
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-brand-ink dark:bg-black flex flex-col">{SidebarInner}</aside>
        </div>
      )}
      <main className="flex-1 md:ml-64">
        <header className="bg-white dark:bg-neutral-900 border-b border-brand-line dark:border-neutral-800 px-4 lg:px-8 py-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button className="md:hidden text-brand-ink dark:text-white" onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="w-5 h-5" /></button>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] bg-brand-red text-white px-3 py-1.5 rounded-full" data-testid="owner-mode-badge"><ShieldCheck className="w-3.5 h-3.5" /> Owner Mode</span>
            <div className="text-sm text-brand-ink/60 dark:text-white/50 hidden sm:block">Signed in as <span className="text-brand-ink dark:text-white font-medium">{user?.email}</span></div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/dashboard/notifications" onClick={markSeen} className="relative p-2 rounded-full border border-brand-line dark:border-neutral-700 text-brand-ink dark:text-white hover:border-brand-red transition-colors" data-testid="notif-bell" aria-label="Notifications">
              <Bell className="w-4 h-4" />
              {unread > 0 && <span className="absolute -top-1 -right-1 bg-brand-red text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid="notif-bell-count">{unread}</span>}
            </Link>
            <button onClick={toggle} className="p-2 rounded-full border border-brand-line dark:border-neutral-700 text-brand-ink dark:text-white hover:border-brand-red transition-colors" data-testid="theme-toggle" aria-label="Toggle theme">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
        </header>
        <div className="p-4 lg:p-8 text-brand-ink dark:text-neutral-100"><Outlet /></div>
      </main>
    </div>
  );
}
